/** The operators a page names, for its create dialogs (D9).
 *
 * A create dialog pre-selects the page's tenant only when the page names exactly
 * one operator, and features every operator the page names at the top of its
 * tenant list. The page hands the dialog its EFFECTIVE tenant (URL > preset >
 * console scope, already merged by `useGridFilters` / `usePageTenant`), so the
 * dialog never consults the scope on its own once it has a `pageTenants` list.
 *
 * The raw value is parsed as a comma list (`parseTenantIds`, `?tenant=12,845`).
 * Sentinels (`all`, `system`, `null`, `none`, `0`) name no operator and yield
 * nothing; so does a hand-edited garbage value.
 */
import type { TenantPickerValue } from '@/components/TenantPicker/TenantPicker';

import { parseTenantIds } from './tenantIds';

/** `raw` → the operators it names, ascending and de-duplicated, as picker
 *  values. `known` values (the scope, the page's picker value) lend their name
 *  and code to the matching id; the rest stay nameless (`name: ''`), which
 *  `TenantPicker` resolves itself. */
export function pageTenantsFrom(
  raw: string | null | undefined,
  known: ReadonlyArray<TenantPickerValue | null | undefined> = [],
): TenantPickerValue[] {
  return parseTenantIds(raw).map((id) => {
    const k = known.find((v) => v?.id === id);
    return k ? { id, name: k.name, ...(k.code ? { code: k.code } : {}) } : { id, name: '' };
  });
}

/** What a create dialog's tenant field opens on: the page's operator when it
 *  names exactly one, otherwise nothing. */
export function singlePageTenant(
  pageTenants: readonly TenantPickerValue[] | undefined,
): TenantPickerValue | null {
  return pageTenants?.length === 1 ? (pageTenants[0] ?? null) : null;
}
